import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import {
  formatChangePct,
  formatMonthShort,
} from "@/components/deal-detail/website-analytics/utils";

type Growth = DealTransformed["websiteTrafficGrowth"];

/**
 * Website traffic growth for one pipeline row.
 *
 * Follows the same rules as the deal widget's trend chip: an em-dash when no
 * trend can be derived, and never a fabricated 0%. A deal with analytics off,
 * with no provider coverage, or with only one month of history all read as
 * "we don't know" rather than "flat".
 */
export function TrafficGrowthCell({ growth }: { growth: Growth }) {
  if (!growth || !growth.direction) {
    return <span className="text-xs text-muted-foreground/40">—</span>;
  }

  const pct = formatChangePct(growth.change_pct);
  const Icon =
    growth.direction === "up"
      ? ArrowUp
      : growth.direction === "down"
        ? ArrowDown
        : Minus;

  const label = [
    growth.current_month ? formatMonthShort(growth.current_month) : null,
    growth.previous_month ? `vs ${formatMonthShort(growth.previous_month)}` : null,
    growth.previous_is_adjacent ? null : "(gap between)",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      title={label || undefined}
      className={cn(
        "inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs font-medium tabular-nums",
        growth.direction === "up" &&
          "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
        growth.direction === "down" &&
          "bg-rose-500/10 text-rose-600 dark:text-rose-400",
        growth.direction === "flat" && "bg-muted/50 text-muted-foreground"
      )}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {/* A zero baseline gives a direction but no meaningful percentage. */}
      {pct ?? (growth.direction === "flat" ? "Flat" : "—")}
    </span>
  );
}

export default TrafficGrowthCell;
